// Admin view for internship-training-program certificate requests. Buyers
// request these from CertificateRequestCard.jsx on their purchase page;
// marking one "mailed" here emails the buyer and updates the status they see.
import {
  TagIcon,
  CheckIcon,
  ClockIcon,
  MailIcon,
  React,
  RefreshCwIcon,
  SearchIcon,
  UploadIcon,
  UserIcon,
  XIcon,
  motion,
} from "../vendor/runtime.js";
import { api } from "../services/api.js";

const STATUS_META = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-800 border-amber-200", Icon: ClockIcon },
  mailed: { label: "Mailed", color: "bg-green-100 text-green-800 border-green-200", Icon: CheckIcon },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.color}`}>
      <Icon className={"w-3.5 h-3.5"} />
      {meta.label}
    </span>
  );
};

const CertificateRequestsPage = () => {
  const [requests, setRequests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");

  const [activeRequest, setActiveRequest] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [certificateFile, setCertificateFile] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const [modalError, setModalError] = React.useState(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await api.get("/certificate-requests");
      setRequests(res.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching certificate requests:", err);
      setError("Failed to load certificate requests");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchRequests();
  }, []);

  const counts = {
    pending: requests.filter((r) => r.status === "pending").length,
    mailed: requests.filter((r) => r.status === "mailed").length,
  };

  const filtered = requests.filter((r) => {
    const q = search.toLowerCase();
    const matchesSearch =
      r.full_name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      r.product_title?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === "all" || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openModal = (request) => {
    setActiveRequest(request);
    setCertificateFile(null);
    setModalError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActiveRequest(null);
    setCertificateFile(null);
    setModalError(null);
  };

  const markAsMailed = async () => {
    if (!activeRequest || sending) return;
    setSending(true);
    setModalError(null);
    try {
      const formData = new FormData();
      if (certificateFile) formData.append("certificate", certificateFile);
      await api.post(`/certificate-requests/${activeRequest.id}/mark-mailed`, formData);
      closeModal();
      fetchRequests();
    } catch (err) {
      console.error("Error marking certificate as mailed:", err);
      setModalError(err.response?.data?.error || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };
  const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  if (loading) {
    return (
      <div className={"admin-page flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100"}>
        <RefreshCwIcon className={"w-10 h-10 text-purple-600 animate-spin"} />
      </div>
    );
  }

  return (
    <div className={"admin-page bg-gradient-to-br from-purple-50 via-purple-100 to-purple-50"}>
      <main className="admin-main">
        <motion.div initial={"hidden"} animate={"visible"} variants={fadeUp} className={"mb-10"}>
          <span className={"inline-block py-1 px-3 mb-3 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"}>
            {"Certificates"}
          </span>
          <h1 className={"text-3xl md:text-4xl font-bold text-gray-900 mb-2"}>
            {"Certificate "}
            <span className={"bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent"}>
              {"Requests"}
            </span>
          </h1>
          <p className={"text-gray-600"}>{"Internship training program certificate requests from buyers."}</p>
        </motion.div>

        <motion.div initial={"hidden"} animate={"visible"} variants={stagger} className={"grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8"}>
          {[
            { key: "pending", label: "Pending", value: counts.pending, color: "bg-amber-100 text-amber-700", hoverBorder: "hover:border-amber-300", Icon: ClockIcon },
            { key: "mailed", label: "Mailed", value: counts.mailed, color: "bg-green-100 text-green-700", hoverBorder: "hover:border-green-300", Icon: CheckIcon },
          ].map(({ key, label, value, color, hoverBorder, Icon }) => (
            <motion.div
              key={key}
              variants={fadeUp}
              className={`bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${hoverBorder}`}
            >
              <div className={"flex items-start justify-between gap-3"}>
                <div className={"flex-1 min-w-0"}>
                  <p className={"text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 truncate"}>{label}</p>
                  <div className={"text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight"}>{value}</div>
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={"w-5 h-5 sm:w-6 sm:h-6"} />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div initial={"hidden"} animate={"visible"} variants={fadeUp} className={"mb-8"}>
          <div className={"bg-white rounded-2xl shadow-lg p-6 border border-gray-100"}>
            <div className={"flex flex-col lg:flex-row gap-4"}>
              <div className={"flex-1 relative"}>
                <SearchIcon className={"absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5"} />
                <input
                  type={"text"}
                  placeholder={"Search by name, email, or product..."}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={"w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={"bg-white border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"}
              >
                <option value={"all"}>{"All Status"}</option>
                <option value={"pending"}>{"Pending"}</option>
                <option value={"mailed"}>{"Mailed"}</option>
              </select>
              <button
                onClick={fetchRequests}
                className={"bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 transition duration-200 flex items-center justify-center"}
              >
                <RefreshCwIcon className={"w-5 h-5 mr-2"} />
                {"Refresh"}
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div initial={"hidden"} animate={"visible"} variants={fadeUp}>
          <div className={"bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"}>
            {error ? (
              <div className={"p-8 text-center text-red-600"}>{error}</div>
            ) : filtered.length === 0 ? (
              <div className={"p-12 text-center"}>
                <TagIcon className={"w-12 h-12 text-gray-300 mx-auto mb-3"} />
                <p className={"text-gray-500"}>{"No certificate requests found."}</p>
              </div>
            ) : (
              <div className={"overflow-x-auto"}>
                <table className={"min-w-full divide-y divide-gray-200"}>
                  <thead className={"bg-gray-50"}>
                    <tr>
                      <th className={"px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"Student"}</th>
                      <th className={"px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"DOB"}</th>
                      <th className={"px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"Product"}</th>
                      <th className={"px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"Status"}</th>
                      <th className={"px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"Requested"}</th>
                      <th className={"px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"Action"}</th>
                    </tr>
                  </thead>
                  <tbody className={"divide-y divide-gray-100"}>
                    {filtered.map((r) => (
                      <tr key={r.id} className={"hover:bg-gray-50 transition-colors"}>
                        <td className={"px-6 py-4"}>
                          <div className={"flex items-center"}>
                            <div className={"w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0"}>
                              <UserIcon className={"w-4 h-4 text-purple-600"} />
                            </div>
                            <div className={"min-w-0"}>
                              <div className={"text-sm font-medium text-gray-900 truncate"}>{r.full_name}</div>
                              <div className={"text-xs text-gray-500 flex items-center truncate"}>
                                <MailIcon className={"w-3 h-3 mr-1 flex-shrink-0"} />
                                {r.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={"px-6 py-4 text-sm text-gray-600 whitespace-nowrap"}>
                          {new Date(r.dob).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" })}
                        </td>
                        <td className={"px-6 py-4 text-sm text-gray-600 max-w-xs truncate"}>{r.product_title}</td>
                        <td className={"px-6 py-4"}>
                          <StatusBadge status={r.status} />
                        </td>
                        <td className={"px-6 py-4 text-sm text-gray-500 whitespace-nowrap"}>
                          {new Date(r.created_at).toLocaleDateString([], { month: "short", day: "numeric" })}
                        </td>
                        <td className={"px-6 py-4 text-right"}>
                          <button
                            onClick={() => openModal(r)}
                            className={"px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"}
                          >
                            {r.status === "mailed" ? "View" : "Process"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      </main>

      {modalOpen && activeRequest && (
        <div className={"fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={"bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"}
          >
            <div className={"px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0"}>
              <h3 className={"text-lg font-bold text-gray-900"}>{"Certificate Request"}</h3>
              <button onClick={closeModal} className={"p-2 rounded-lg hover:bg-gray-100 transition-colors"}>
                <XIcon className={"w-5 h-5 text-gray-500"} />
              </button>
            </div>

            <div className={"p-6 space-y-4"}>
              <div className={"flex items-center justify-between"}>
                <StatusBadge status={activeRequest.status} />
              </div>
              <div className={"space-y-2 text-sm"}>
                <div className={"flex justify-between"}>
                  <span className={"text-gray-500"}>{"Name"}</span>
                  <span className={"font-medium text-gray-900"}>{activeRequest.full_name}</span>
                </div>
                <div className={"flex justify-between"}>
                  <span className={"text-gray-500"}>{"Email"}</span>
                  <span className={"font-medium text-gray-900"}>{activeRequest.email}</span>
                </div>
                <div className={"flex justify-between"}>
                  <span className={"text-gray-500"}>{"DOB"}</span>
                  <span className={"font-medium text-gray-900"}>
                    {new Date(activeRequest.dob).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" })}
                  </span>
                </div>
                <div className={"flex justify-between"}>
                  <span className={"text-gray-500"}>{"Product"}</span>
                  <span className={"font-medium text-gray-900 text-right"}>{activeRequest.product_title}</span>
                </div>
                <div className={"flex justify-between"}>
                  <span className={"text-gray-500"}>{"Account"}</span>
                  <span className={"font-medium text-gray-900 text-right"}>
                    {activeRequest.account_name} ({activeRequest.account_email})
                  </span>
                </div>
              </div>

              {activeRequest.status === "pending" ? (
                <div className={"border-t border-gray-200 pt-4"}>
                  <label className={"block text-xs font-medium text-gray-600 mb-2"}>
                    {"Attach certificate file (optional)"}
                  </label>
                  <label
                    className={
                      "flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors text-sm text-gray-600"
                    }
                  >
                    <UploadIcon className={"w-4 h-4"} />
                    {certificateFile ? certificateFile.name : "Choose a PDF or image"}
                    <input
                      type={"file"}
                      className={"hidden"}
                      onChange={(e) => setCertificateFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  {modalError && <p className={"text-xs text-red-600 mt-2"}>{modalError}</p>}
                  <button
                    onClick={markAsMailed}
                    disabled={sending}
                    className={
                      "w-full mt-4 flex items-center justify-center px-4 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed transition-colors"
                    }
                  >
                    {sending ? (
                      <RefreshCwIcon className={"w-4 h-4 animate-spin"} />
                    ) : (
                      <>
                        <MailIcon className={"w-4 h-4 mr-2"} />
                        {"Send Email & Mark as Mailed"}
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className={"border-t border-gray-200 pt-4 text-sm text-gray-500"}>
                  {"Mailed on "}
                  {new Date(activeRequest.mailed_at).toLocaleString([], {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export { CertificateRequestsPage };
